// OPT4 Dynamic Adaptive Rescue · side protection (ISS-042, 4 Nov 2026): the pair's protection record from the backend
// (options_hedge/opt4_protect via GET /trades/{id}/pairs/{n}/rescue → rescue.opt4p). The CONFIRMED position's figures
// (exact expiry payoff after every charge, from the fills) are kept apart from a candidate's PROJECTED completion.
// FULLY PROTECTED is shown only from the backend's verified flag. Nothing here places or changes an order.
import React from "react";
import { px } from "@/components/options/OptionsCommon";

const num = (v) => (v == null || v === "" || !Number.isFinite(Number(v)) ? null : Number(v));
export const money = (v) => (v === "UNBOUNDED" ? "unbounded ↑" : v === "-UNBOUNDED" ? "unbounded loss" : num(v) == null ? "—"
  : `${num(v) >= 0 ? "" : "−"}₹${Math.abs(num(v)).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`);
const cls = (v) => (num(v) == null ? "text-[#FF3B30]" : num(v) > 0 ? "text-[#008a04]" : "text-[#FF3B30]");
const when = (epoch) => (epoch ? new Date(epoch * 1000).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour12: false }) : "—");

export const SIDE_STATE = {
  NOT_PROTECTED: ["not protected", "text-[#FF3B30]"],
  PROTECTION_PENDING: ["protection pending", "text-[#2563EB]"],
  PARTIALLY_PROTECTED: ["partially protected", "text-[#B45309]"],
  PROTECTED: ["protected", "text-[#008a04]"],
  NO_VALID_OPPORTUNITY: ["no valid opportunity", "text-[#B45309]"],
};
const REJECT = {
  NO_FEASIBLE_COMPLETION: "the other side could not then be completed to a positive floor",
  SIDE_FLOOR_NOT_POSITIVE: "the side's floor would not be above ₹0 after costs",
  SIDE_NOT_BOUNDED: "the side would still lose without limit",
  OPPOSITE_SIDE_WORSE: "it would worsen the other side",
  FLOOR_NOT_POSITIVE: "the combined minimum would not be above ₹0",
};

/** The portfolio label: only the backend's verified result may say FULLY PROTECTED. */
export function portfolioLabel(p) {
  const v = (p && p.view) || {};
  if (p?.final === "FULLY_PROTECTED" && v.full) return ["FULLY PROTECTED · POSITIVE FLOOR", "text-[#008a04]"];
  if (p?.final === "MAX_ADJUSTMENTS_NOT_PROTECTED") return ["MAX_ADJUSTMENTS — NOT FULLY PROTECTED", "text-[#FF3B30]"];
  if (p?.final === "PROTECTION_INVALIDATED") return ["PROTECTION INVALIDATED — review required", "text-[#FF3B30]"];
  const prot = ["UPPER", "LOWER"].filter((s) => v.sides?.[s]?.state === "PROTECTED");
  if (prot.length === 1) return [`${prot[0] === "UPPER" ? "CE" : "PE"} side protected · NOT fully protected`, "text-[#B45309]"];
  return ["NOT PROTECTED", "text-[#FF3B30]"];
}

const orderText = (o) => `${o.dir === "S" ? "SELL" : "BUY"} ${px(o.strike)} ${o.side} ×${o.qty}${num(o.price) != null ? ` @ ${px(o.price)}` : ""}`;

export default function Opt4ProtectPanel({ opt4p, adjustments = [], cfg = {}, pairNo }) {
  const p = opt4p || {};
  const v = p.view || {};
  const [label, lcls] = portfolioLabel(p);
  const scan = p.last_scan || {};
  const best = (scan.ranked || [])[0];
  const filled = adjustments.filter((a) => a.filled).length;
  const cap = cfg.opt4p_max_adjustments ?? 4;
  return (
    <div className="space-y-1 rounded-sm border border-[var(--term-border)] px-2 py-1" data-testid={`opt4p-panel-${pairNo}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold">OPT4 side protection</span>
        <span className={`font-semibold ${lcls}`} data-testid={`opt4p-label-${pairNo}`}>{label}</span>
        <span className="text-[var(--term-muted)]">adjustments {filled} / {cap}</span>
      </div>
      <div className="flex flex-wrap gap-3" data-testid={`opt4p-sides-${pairNo}`}>
        {[["UPPER", "CE"], ["LOWER", "PE"]].map(([s, t]) => {
          const ss = p.side_state?.[s] || {};
          const [sl, sc] = SIDE_STATE[ss.state] || [ss.state || "—", ""];
          const fl = v.sides?.[s]?.floor;
          return <span key={s} data-testid={`opt4p-side-${t}-${pairNo}`}>{t}: <b className={sc}>{sl}</b>
            <span className="text-[var(--term-muted)]"> · tail floor {fl == null ? "unbounded loss" : money(fl)}</span>
            {ss.reason && ss.state !== "PROTECTED" && <span className="text-[var(--term-muted)]"> · {ss.reason}</span>}</span>;
        })}
      </div>
      <div className="mono" data-testid={`opt4p-verified-${pairNo}`}>
        Verified (confirmed fills, after all charges): min <b className={cls(v.min_net)}>{money(v.min_net)}</b> · max {money(v.max_net)}
        {" "}· band {p.band ? `${px(p.band[0])}–${px(p.band[1])}` : "—"} avg {money(v.band_avg)}
        <span className="text-[var(--term-muted)]"> · est. exit charges {money(v.exit_estimate)} · {when(v.at)}</span>
      </div>
      {!p.final && <div className="text-[var(--term-muted)]" data-testid={`opt4p-scan-${pairNo}`}>
        Last scan {when(scan.at)} · {scan.contracts ?? 0} quoted contracts · {scan.considered ?? 0} candidates (executable bid / ask)
        {Object.keys(scan.rejected || {}).length > 0 && <> · refused: {Object.entries(scan.rejected).map(([k, n]) => `${n} ${REJECT[k] || k}`).join("; ")}</>}
      </div>}
      {!p.final && best && <div data-testid={`opt4p-best-${pairNo}`}>
        Best: <span className="mono">{(best.orders || []).map(orderText).join(" + ")}</span>
        {" "}· {best.min_basis === "PROJECTED_COMPLETION"
          ? <>PROJECTED min {money(best.projected_min)} if later completed by <span className="mono">{(best.completion?.orders || []).map(orderText).join(" + ")}</span> (not verified)</>
          : <>min {money(best.projected_min)} once filled (verified only after the fills)</>}
        {" "}· band kept {best.band_kept_pct ?? "—"}%</div>}
      {p.final && <div className="text-[var(--term-muted)]">Scanning stopped ({p.final}) at {when(p.final_at || p.invalidated?.at)} — OPT4 sends no further adjustments for this pair.</div>}
    </div>
  );
}
